
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthResponse } from '../../types/user';
import { APIHOST } from '../../common';
import AuthDialog from './AuthDialog';
import { useNavigate } from 'react-router';
import { User } from '../../types/user';
import { Message } from '@/src/types/message';
import Cookies from 'js-cookie';

interface AuthContextType {
  currentUser: User | null;
  isLoggedIn: boolean;
  userType: string | null;
  userToken: string | null;
  showAuthDialog: (mode?: 'login' | 'register') => void;
  hideAuthDialog: () => void;
  login: (authResponse: AuthResponse) => void;
  logout: () => void;
  loadingUser: boolean;
  unreadMessages?: Message[];
  refreshUnreadMessages: () => void;
  getToken: () => string | null;
  setCurrentUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const getToken = (): string | null => {
  return Cookies.get('authToken') || null; // Ensure cookie is read to sync with localStorage
  // return localStorage.getItem('authToken');
};

export const setToken = (token: string) => {
  Cookies.set('authToken', token, { expires: 7, path: '/', secure: process.env.NODE_ENV === 'production' }); // Set cookie for 7 days
  // localStorage.setItem('authToken', token);
};

export const clearToken = () => {
  Cookies.remove('authToken');
  // localStorage.removeItem('authToken');
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authDialogMode, setAuthDialogMode] = useState<'login' | 'register'>('login');
  const [loadingUser, setLoadingUser] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState<Message[] | undefined>(undefined);
  const navigate = useNavigate();

  const refreshUnreadMessages = async () => {
    const token = getToken();
    if (!token) {
      setUnreadMessages(undefined);
      return;
    }

    try {
      const response = await fetch(`${APIHOST}/api/messages/unread`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch unread messages');
      }

      const data = await response.json();
      setUnreadMessages(data);
    } catch (error) {
      console.error('Error fetching unread messages:', error);
      setUnreadMessages(undefined);
    }
  }

  // Verify user authentication status with API
  useEffect(() => {
    
    const verifyUser = async () => {
      const token = getToken();
      
      if (!token) {
        console.debug('No auth token found, skipping user verification');
        setLoadingUser(false);
        return;
      }

      try {
    
        const response = await fetch(`${APIHOST}/api/users/me?populate=role`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Authentication verification failed');
        }

        // Optional: Update user data if API returns updated info
        const userData = await response.json();
        if (userData) {
          // Update currentUser if needed
          setCurrentUser(userData);
          setLoadingUser(false);
        }
      } catch (error) {
        console.error('User verification failed:', error);
        // Clear auth data and set user to null
        clearToken();
        setCurrentUser(null);
      } finally {
        setLoadingUser(false);
      }

    };

   
      verifyUser();
    
  }, []);

  const login = (authResponse: AuthResponse) => {
    setCurrentUser(authResponse.user);
    setToken(authResponse.jwt);
    setAuthDialogOpen(false);
    if (authResponse.user.userType === 'internal') {
      console.debug('Admin user logged in:', authResponse.user);
      navigate("/admin");
    } else {
      navigate("/");
    }
  };

  const logout = () => {
    setCurrentUser(null);
    clearToken();
    setAuthDialogOpen(false);
    navigate("/");
  };

  const showAuthDialog = (mode: 'login' | 'register' = 'login') => {
    setAuthDialogMode(mode);
    setAuthDialogOpen(true);
  };

  const hideAuthDialog = () => {
    setAuthDialogOpen(false);
  };

  const isLoggedIn = !!currentUser;
  const userType = currentUser?.userType || null;

  const contextValue: AuthContextType = {
    currentUser,
    isLoggedIn,
    userType,
    showAuthDialog,
    hideAuthDialog,
    login,
    logout,
    loadingUser,
    userToken: getToken(),
    unreadMessages,
    refreshUnreadMessages,
    getToken,
    setCurrentUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <AuthDialog
        open={authDialogOpen}
        onClose={hideAuthDialog}
        onAuthSuccess={login}
        initialMode={authDialogMode}
      />
    </AuthContext.Provider>
  );
};